import { Divider } from "@msuite/ui/divider";
import { useTranslation } from "@msuite/ui/localization";
import { OverflowList } from "@msuite/ui/overflow-list";
import { Popover } from "@msuite/ui/popover";
import { Tag } from "@msuite/ui/tag";
import { filterNonNull } from "@msuite/utils/array";
import { getTeamCapabilityTranslationKey } from "@msuite/utils/organizations";
import type { Enums, Nullable } from "@msuite/utils/types";
import { Fragment } from "react";

/** Props Interface */
interface TeamCapabilitiesCellProps {
	capabilities: Nullable<Array<Nullable<Enums<"teamCapability">>>>;
}

/**
 * A team's capabilities as tags in one line, the ones that do not fit folded into a "+ n" tag
 * whose popover names them — the projects table's managers column, for capabilities.
 */
export function TeamCapabilitiesCell({ capabilities }: TeamCapabilitiesCellProps) {
	/** Context */
	const { t } = useTranslation();

	/** Computed */
	const items = filterNonNull(capabilities ?? []);

	/** Render */
	return (
		<div className="absolute inset-y-0 inset-x-2 h-stack items-center">
			<OverflowList
				items={items}
				renderItem={(capability) => (
					<Tag
						size="xs"
						withStatusIndicator={false}
						color="gray"
					>
						{t(getTeamCapabilityTranslationKey(capability))}
					</Tag>
				)}
				renderOverflowItem={({ overflowCount, hiddenItems }) => (
					<Popover
						trigger="hover"
						render={() => (
							<div className="v-stack">
								{hiddenItems.map((capability) => (
									<Fragment key={capability}>
										<p>{t(getTeamCapabilityTranslationKey(capability))}</p>
										<Divider variant="card" />
									</Fragment>
								))}
							</div>
						)}
					>
						<Tag
							size="xs"
							color="gray"
							withStatusIndicator={false}
						>
							+ {overflowCount}
						</Tag>
					</Popover>
				)}
			/>
		</div>
	);
}
